import { Component, computed, input, output } from '@angular/core';
import { HlmComboboxImports } from '@mb/ui/combobox';
import { matchesSearch, type SearchSelectOption } from './search-select-match';

/**
 * A dropdown you can type into (UI-018).
 *
 * The native select it replaces only jumps to the first entry starting with
 * the letter pressed; with a few hundred contracts or a deep organigram that
 * means scrolling. This filters as you type, on any part of the label or the
 * hint, and is driven by keyboard (arrows, Enter, Escape) as well as by mouse.
 *
 * Two modes. With `remote` off it filters `options` itself. With `remote` on
 * it filters nothing and reports what was typed through `searchChange`; the
 * caller fetches and hands back `options` — the contract picker does that, as
 * a tenant's contracts are not a list to ship whole.
 *
 * Works with ids, not objects, so a caller can keep the choice in the address.
 * `selectedLabel` names the choice when it is not among the current `options`,
 * which with a remote search is the normal case once the term has changed.
 */
@Component({
  selector: 'app-search-select',
  standalone: true,
  imports: [HlmComboboxImports],
  host: { class: 'block' },
  template: `
    <hlm-combobox
      [value]="selectedOption()"
      [itemToString]="labelOf"
      [isItemEqualToValue]="sameOption"
      [filter]="filter()"
      [disabled]="disabled()"
      (valueChange)="pick($event)"
      (searchChange)="searchChange.emit($event)"
    >
      <hlm-combobox-input
        [inputId]="inputId()"
        [placeholder]="placeholder()"
        [triggerAriaLabel]="placeholder()"
        [showClear]="clearLabel() !== null"
        [clearAriaLabel]="clearLabel() ?? ''"
        [attr.data-testid]="testId()"
      />
      <div *hlmComboboxPortal hlmComboboxContent>
        @if (loading()) {
          <div hlmComboboxStatus>{{ loadingText() }}</div>
        }
        <hlm-combobox-empty>{{ emptyText() }}</hlm-combobox-empty>
        <div hlmComboboxList>
          @for (option of options(); track option.id) {
            <hlm-combobox-item [value]="option">
              <!-- Wrapped, not cut: a unit's name is often longer than the field. -->
              <span class="flex min-w-0 flex-col break-words">
                <span>{{ option.label }}</span>
                @if (option.hint) {
                  <span class="text-xs text-muted-foreground">
                    {{ option.hint }}
                  </span>
                }
              </span>
            </hlm-combobox-item>
          }
        </div>
        @if (moreText(); as more) {
          <div hlmComboboxStatus>{{ more }}</div>
        }
      </div>
    </hlm-combobox>
  `,
})
export class SearchSelectComponent {
  readonly options = input.required<readonly SearchSelectOption[]>();
  readonly value = input<string | null>(null);
  readonly selectedLabel = input<string | null>(null);
  readonly placeholder = input<string>('');
  readonly emptyText = input<string>('');
  readonly loadingText = input<string>('');
  /** Shown under the list when the caller knows there are more matches. */
  readonly moreText = input<string | null>(null);
  readonly loading = input(false);
  readonly disabled = input(false);
  readonly remote = input(false);
  /**
   * Names the button that empties the field; without it there is none. For a
   * field where nothing chosen means something — a timezone left to inherit.
   */
  readonly clearLabel = input<string | null>(null);
  readonly inputId = input<string>(`app-search-select-${nextId++}`);
  readonly testId = input<string | null>(null);

  readonly valueChange = output<string | null>();
  readonly searchChange = output<string>();

  /**
   * The chosen entry as the combobox wants it: an object, found among the
   * options or rebuilt from the label the caller knows it by.
   */
  protected readonly selectedOption = computed<SearchSelectOption | null>(() => {
    const id = this.value();
    if (!id) return null;
    return (
      this.options().find((option) => option.id === id) ?? {
        id,
        label: this.selectedLabel() ?? id,
      }
    );
  });

  protected readonly filter = computed(() =>
    this.remote()
      ? () => true
      : (option: SearchSelectOption, search: string) =>
          matchesSearch(option, search),
  );

  // Trimmed: a tree list indents its labels, and the field shows one alone.
  protected readonly labelOf = (option: SearchSelectOption) =>
    option.label.trim();

  protected readonly sameOption = (
    option: SearchSelectOption,
    selected: SearchSelectOption | null | undefined,
  ) => option.id === selected?.id;

  protected pick(option: SearchSelectOption | null | undefined): void {
    const id = option?.id ?? null;
    if (id !== this.value()) this.valueChange.emit(id);
  }
}

let nextId = 0;
